import React, { useEffect, useState } from 'react';
import { ChevronLeft, ChevronRight, Images, Loader2 } from 'lucide-react';

import { listWikiImageCandidates, type WikiImageCandidate } from '../../api/wikiImageApi';

interface WikiImagePickerProps {
  wikiQuery?: string;
  currentImageUrl?: string;
  onSelect: (url: string) => void;
}

/**
 * Permite cambiar la imagen Wikimedia/Wikipedia elegida (no solo la primera).
 */
export function WikiImagePicker({ wikiQuery, currentImageUrl, onSelect }: WikiImagePickerProps) {
  const query = String(wikiQuery || '').trim();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [candidates, setCandidates] = useState<WikiImageCandidate[]>([]);
  const [index, setIndex] = useState(0);

  useEffect(() => {
    if (!open || !query) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    void listWikiImageCandidates(query, 16)
      .then((list) => {
        if (cancelled) return;
        setCandidates(list);
        const current = String(currentImageUrl || '').trim();
        const found = list.findIndex((c) => c.url.split('?')[0] === current.split('?')[0]);
        setIndex(found >= 0 ? found : 0);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : 'No se pudieron cargar imágenes');
        setCandidates([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, query, currentImageUrl]);

  if (!query) return null;

  const active = candidates[index];

  return (
    <div className="wiki-image-picker">
      <button
        type="button"
        className="wiki-image-picker__toggle"
        onClick={() => setOpen((v) => !v)}
      >
        <Images className="h-3.5 w-3.5" aria-hidden />
        {open ? 'Ocultar alternativas' : 'Cambiar imagen de Wikipedia'}
      </button>

      {open ? (
        <div className="wiki-image-picker__panel">
          {loading ? (
            <p className="wiki-image-picker__status">
              <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
              Buscando imágenes…
            </p>
          ) : null}
          {error ? <p className="wiki-image-picker__error">{error}</p> : null}
          {!loading && !error && candidates.length === 0 ? (
            <p className="wiki-image-picker__status">No hay otras imágenes para esta búsqueda.</p>
          ) : null}
          {!loading && active ? (
            <>
              <div className="wiki-image-picker__preview">
                <img src={active.url} alt={active.title || 'Candidata Wikimedia'} />
              </div>
              <p className="wiki-image-picker__meta">
                {index + 1} / {candidates.length}
                {active.title ? ` · ${active.title.replace(/^File:|^Archivo:/i, '')}` : ''}
              </p>
              <div className="wiki-image-picker__actions">
                <button
                  type="button"
                  className="wiki-image-picker__nav"
                  disabled={index <= 0}
                  onClick={() => setIndex((i) => Math.max(0, i - 1))}
                  aria-label="Imagen anterior"
                >
                  <ChevronLeft className="h-4 w-4" />
                </button>
                <button
                  type="button"
                  className="wiki-image-picker__use"
                  onClick={() => onSelect(active.url)}
                >
                  Usar esta imagen
                </button>
                <button
                  type="button"
                  className="wiki-image-picker__nav"
                  disabled={index >= candidates.length - 1}
                  onClick={() => setIndex((i) => Math.min(candidates.length - 1, i + 1))}
                  aria-label="Imagen siguiente"
                >
                  <ChevronRight className="h-4 w-4" />
                </button>
              </div>
            </>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
